import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Table } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import { isoToDisplayDate } from '@/pages/accounting/statements/statementMeta';
import { cn } from '@/utils/cnUtils';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    formatPeriod,
    rateLabel,
    RUN_ENTRY_STATUS_CHIP,
} from '../../salariesMeta';
import { useRunDetailDrawer } from './useRunDetailDrawer';

type RunDetailDrawerProps = {
    /** The run to show, by its journal entry id; `null` closes the drawer. */
    journalEntryId: string | null;
    locale: LocaleCode;
    onClose: () => void;
};

const Field: React.FC<{ label: string; value: string; hint?: string }> = ({
    label,
    value,
    hint,
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span className="text-sm font-semibold tabular-nums text-foreground">
            {value}
        </span>
        {hint ? <span className="text-xs text-muted">{hint}</span> : null}
    </div>
);

const NUMERIC_CELL = 'whitespace-nowrap px-3 py-2 text-right tabular-nums';

/**
 * Right-side payroll-run detail drawer (HeroUI Pro `Sheet`) — sibling of the
 * asset detail drawer. Self-served: it fetches the run with its per-employee
 * breakdown.
 *
 * Four things it exists to show:
 *
 * - the **breakdown** the run charged, employee by employee — what was actually
 *   recorded, not a re-computation over today's employee data;
 * - whether its entry has been **posted**. A run is created as a DRAFT and
 *   charges nothing until an accountant posts it, so a drawer that showed only
 *   the figures would let a committed run read as money already owed;
 * - the **journal entry** it produced, linked into the ledger;
 * - the **filing due date** — the 15th of the following month — as information.
 *   Nothing here files or pays anything.
 */
export const RunDetailDrawer: React.FC<RunDetailDrawerProps> = ({
    journalEntryId,
    locale,
    onClose,
}) => {
    const { t } = useTranslation('pages');
    const { run, rates, rows, totals, isLoading, onOpenEntry } =
        useRunDetailDrawer({
            journalEntryId,
            locale,
        });
    const statusChip = run?.entryStatus
        ? RUN_ENTRY_STATUS_CHIP[run.entryStatus]
        : null;

    return (
        <Sheet
            isOpen={journalEntryId !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[680px]">
                    <Sheet.Dialog
                        aria-label={
                            run
                                ? t('salaries.runDetail.titleFor', {
                                      period: formatPeriod(run.period, t),
                                  })
                                : t('salaries.runDetail.title')
                        }
                        {...getNodeTestIdProp('salaries-run-detail-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span
                                        className="text-lg font-semibold"
                                        {...getNodeTestIdProp(
                                            'salaries-run-detail-period',
                                        )}
                                    >
                                        {run
                                            ? formatPeriod(run.period, t)
                                            : t('salaries.runDetail.title')}
                                    </span>
                                    <span className="text-sm font-normal text-muted">
                                        {t('salaries.runDetail.subtitle')}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-5">
                            {isLoading ? (
                                <p className="text-sm text-muted">
                                    {t('salaries.runDetail.loading')}
                                </p>
                            ) : null}

                            {!isLoading && !run ? (
                                <p
                                    className="text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'salaries-run-detail-not-found',
                                    )}
                                >
                                    {t('salaries.runDetail.notFound')}
                                </p>
                            ) : null}

                            {run && totals ? (
                                <>
                                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                                        <Field
                                            label={t(
                                                'salaries.runDetail.employees',
                                            )}
                                            value={String(
                                                totals.paidEmployeeCount,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'salaries.run.columns.gross',
                                            )}
                                            value={totals.grossText}
                                        />
                                        <Field
                                            hint={t(
                                                'salaries.runDetail.employerCostHint',
                                            )}
                                            label={t(
                                                'salaries.summary.employerCost',
                                            )}
                                            value={totals.employerCostText}
                                        />
                                    </div>

                                    {/* Whether the entry behind this run has
                                        actually posted. A DRAFT run has charged
                                        nothing — the register's accrued figures
                                        do not include it — and saying so here is
                                        what stops the breakdown above from
                                        reading as money already owed. */}
                                    {statusChip ? (
                                        <div
                                            className="flex flex-wrap items-center gap-2 text-sm"
                                            {...getNodeTestIdProp(
                                                'salaries-run-detail-entry-status',
                                            )}
                                        >
                                            <span
                                                className={cn(
                                                    'rounded-lg px-2 py-0.5 text-xs font-medium',
                                                    statusChip.className,
                                                )}
                                            >
                                                {t(statusChip.labelKey)}
                                            </span>
                                            <span className="text-muted">
                                                {t(
                                                    run.entryStatus === 'POSTED'
                                                        ? 'salaries.runDetail.postedNote'
                                                        : 'salaries.runDetail.draftNote',
                                                )}
                                            </span>
                                        </div>
                                    ) : null}

                                    {/* The filing deadline, as information. */}
                                    <div
                                        className="flex flex-wrap items-center gap-2 rounded-xl bg-surface-secondary px-3 py-2 text-sm"
                                        {...getNodeTestIdProp(
                                            'salaries-run-detail-due-date',
                                        )}
                                    >
                                        <Icon
                                            aria-hidden
                                            className="text-muted"
                                            icon="gravity-ui:calendar"
                                        />
                                        <span className="text-muted">
                                            {t('salaries.runDetail.dueDate', {
                                                date: isoToDisplayDate(
                                                    run.dueDate,
                                                ),
                                            })}
                                        </span>
                                    </div>

                                    <div
                                        className="shrink-0"
                                        {...getNodeTestIdProp(
                                            'salaries-run-detail-table',
                                        )}
                                    >
                                        <Table>
                                            <Table.ScrollContainer>
                                                <Table.Content
                                                    aria-label={t(
                                                        'salaries.runDetail.tableAria',
                                                    )}
                                                    className="min-w-[560px] text-sm"
                                                >
                                                    <Table.Header>
                                                        <Table.Column
                                                            id="name"
                                                            isRowHeader
                                                        >
                                                            {t(
                                                                'salaries.columns.name',
                                                            )}
                                                        </Table.Column>
                                                        <Table.Column id="gross">
                                                            {t(
                                                                'salaries.run.columns.gross',
                                                            )}
                                                        </Table.Column>
                                                        <Table.Column id="incomeTax">
                                                            {rateLabel(
                                                                'salaries.run.columns.incomeTax',
                                                                rates?.incomeTax,
                                                                t,
                                                            )}
                                                        </Table.Column>
                                                        <Table.Column id="employeePension">
                                                            {rateLabel(
                                                                'salaries.run.columns.employeePension',
                                                                rates?.employeePension,
                                                                t,
                                                            )}
                                                        </Table.Column>
                                                        <Table.Column id="net">
                                                            {t(
                                                                'salaries.run.columns.net',
                                                            )}
                                                        </Table.Column>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {rows.map((row) => (
                                                            <Table.Row
                                                                id={
                                                                    row.employeeId
                                                                }
                                                                key={
                                                                    row.employeeId
                                                                }
                                                                {...getNodeTestIdProp(
                                                                    `salaries-run-detail-row-${row.employeeId}`,
                                                                )}
                                                            >
                                                                <Table.Cell>
                                                                    <span className="flex flex-col">
                                                                        <span className="text-foreground">
                                                                            {
                                                                                row.name
                                                                            }
                                                                        </span>
                                                                        {row.reasonKey ? (
                                                                            <span className="text-xs text-muted">
                                                                                {t(
                                                                                    row.reasonKey,
                                                                                    row.prorationDays ??
                                                                                        {},
                                                                                )}
                                                                            </span>
                                                                        ) : null}
                                                                    </span>
                                                                </Table.Cell>
                                                                {[
                                                                    row.grossText,
                                                                    row.incomeTaxText,
                                                                    row.employeePensionText,
                                                                    row.netText,
                                                                ].map(
                                                                    (
                                                                        value,
                                                                        column,
                                                                    ) => (
                                                                        <Table.Cell
                                                                            key={
                                                                                column
                                                                            }
                                                                        >
                                                                            <span
                                                                                className={cn(
                                                                                    NUMERIC_CELL,
                                                                                    row.isZero
                                                                                        ? 'text-muted'
                                                                                        : 'text-foreground',
                                                                                )}
                                                                            >
                                                                                {
                                                                                    value
                                                                                }
                                                                            </span>
                                                                        </Table.Cell>
                                                                    ),
                                                                )}
                                                            </Table.Row>
                                                        ))}
                                                        {/* The total is the last
                                                            body row, not a
                                                            `Table.Footer`: the
                                                            footer slot renders
                                                            outside the table, and
                                                            the totals must stay
                                                            column-aligned with the
                                                            figures above them. */}
                                                        <Table.Row
                                                            className="border-t-2 border-foreground/50 bg-surface-secondary"
                                                            id="salaries-run-detail-total-row"
                                                            key="salaries-run-detail-total-row"
                                                        >
                                                            <Table.Cell>
                                                                <span className="text-sm font-semibold">
                                                                    {t(
                                                                        'salaries.run.total',
                                                                    )}
                                                                </span>
                                                            </Table.Cell>
                                                            {[
                                                                totals.grossText,
                                                                totals.incomeTaxText,
                                                                totals.employeePensionText,
                                                                totals.netText,
                                                            ].map(
                                                                (
                                                                    value,
                                                                    column,
                                                                ) => (
                                                                    <Table.Cell
                                                                        key={
                                                                            column
                                                                        }
                                                                    >
                                                                        <span className="block whitespace-nowrap text-right text-sm font-bold tabular-nums text-foreground">
                                                                            {
                                                                                value
                                                                            }
                                                                        </span>
                                                                    </Table.Cell>
                                                                ),
                                                            )}
                                                        </Table.Row>
                                                    </Table.Body>
                                                </Table.Content>
                                            </Table.ScrollContainer>
                                        </Table>
                                    </div>

                                    {run.journalEntryId ? (
                                        <div className="flex justify-end">
                                            <Button
                                                onPress={() =>
                                                    onOpenEntry(
                                                        run.journalEntryId as string,
                                                        run.period,
                                                    )
                                                }
                                                variant="outline"
                                                {...getNodeTestIdProp(
                                                    'salaries-run-detail-open-entry',
                                                )}
                                            >
                                                <Icon
                                                    aria-hidden
                                                    icon="gravity-ui:arrow-up-right-from-square"
                                                />
                                                {t(
                                                    'salaries.runDetail.openEntry',
                                                )}
                                            </Button>
                                        </div>
                                    ) : null}
                                </>
                            ) : null}
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
